import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  Node,
  Edge,
  applyNodeChanges,
  applyEdgeChanges,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";

type State = {
  allNodes: Node[];
  nodes: Node[];
  edges: Edge[];
  isLoading: boolean;
  nodeToFocusOn?: { id: string; isCollapsed: boolean };
  isDialogOpen: boolean;
  areaDataForDialog: { id: string; name: string };
  searchedNodeId: string | null;
  visibleNodesRef: Map<string, boolean>;
  childrenCollapsedStatus: Map<string, boolean>;
  wasSearchedNodeCleared: boolean;
};

type Action = {
  setAllNodes: (nodes: State["allNodes"]) => void;
  setNodes: (nodes: State["nodes"]) => void;
  setEdges: (edges: State["edges"]) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  updateIsLoading: (isLoading: State["isLoading"]) => void;
  setNodeToFocusOn: (nodesToFocusOn: State["nodeToFocusOn"]) => void;
  setIsDialogOpen: (isDialogOpen: State["isDialogOpen"]) => void;
  setAreaDataForDialog: (areaDataForDialog: State["areaDataForDialog"]) => void;
  setSearchedNodeId: (searchedNodeId: State["searchedNodeId"]) => void;
  setVisibleNodesRef: (visibleNodesRef: State["visibleNodesRef"]) => void;
  setChildrenCollapsedStatus: (
    childrenCollapsedStatus: State["childrenCollapsedStatus"]
  ) => void;
  setWasSearchedNodeCleared: (
    wasSearchedNodeCleared: State["wasSearchedNodeCleared"]
  ) => void;
};

const useOrgchartStore = create<State & Action>()(
  devtools((set, getState) => ({
    allNodes: [],
    nodes: [],
    edges: [],
    isLoading: true,
    isDialogOpen: false,
    areaDataForDialog: { id: "", name: "" },
    searchedNodeId: null,
    visibleNodesRef: new Map<string, boolean>(),
    childrenCollapsedStatus: new Map<string, boolean>(),
    wasSearchedNodeCleared: false,
    setAllNodes: (allNodes) => set({ allNodes }),
    onNodesChange: (changes) =>
      set({ nodes: applyNodeChanges(changes, getState().nodes) }),
    onEdgesChange: (changes) =>
      set({ edges: applyEdgeChanges(changes, getState().edges) }),
    setNodes: (nodes) => set({ nodes }),
    setEdges: (edges) => set(() => ({ edges })),
    updateIsLoading: (isLoading) => set(() => ({ isLoading })),
    setNodeToFocusOn: (nodeToFocusOn) => set({ nodeToFocusOn }),
    setIsDialogOpen: (isDialogOpen) => set({ isDialogOpen }),
    setAreaDataForDialog: (areaDataForDialog) => set({ areaDataForDialog }),
    setSearchedNodeId: (searchedNodeId) => set({ searchedNodeId }),
    setVisibleNodesRef: (visibleNodesRef) => set({ visibleNodesRef }),
    setChildrenCollapsedStatus: (childrenCollapsedStatus) =>
      set({ childrenCollapsedStatus }),
    setWasSearchedNodeCleared: (wasSearchedNodeCleared) =>
      set({ wasSearchedNodeCleared }),
  }))
);

export default useOrgchartStore;
